import ErrorPage from 'components/alerts/error-page'
import ErrorBoundary from 'components/layout/error-boundary'
import Navigation from 'components/layout/navigation'
import NavigationMobile from 'components/layout/navigation-mobile'
import { useApp } from 'context/app-context'
import { useOnboarding } from 'context/onboarding-context'
import classNames from 'helpers/class-names'
import useHubspot from 'hooks/use-hubspot'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FC, ReactNode, useEffect, useState } from 'react'

interface Props {
  children: ReactNode
  full?: boolean
  hideNavigation?: boolean
  className?: string
  mainWrapperClassName?: string
  mainClassName?: string
  error?: string | number
  previous?: string
  previousMessage?: string
}

const Layout: FC<Props> = ({
  children,
  hideNavigation = false,
  full = false,
  className,
  mainWrapperClassName,
  mainClassName,
  error,
  previous,
  previousMessage,
}) => {
  const { isReady } = useRouter()
  const { isVisible: onboardingIsVisible } = useOnboarding()
  const [opacity, setOpacity] = useState(0)
  const {
    navigationCollapsed,
    mobileNavigationClosed,
    setNavigationHidden,
    modal,
  } = useApp()

  useEffect(() => {
    setOpacity(isReady ? 100 : 0)
  }, [isReady])

  useEffect(() => {
    if (typeof hideNavigation !== 'undefined') {
      setNavigationHidden(hideNavigation)
    }
  }, [hideNavigation])

  useHubspot()

  return (
    <div
      className="duration-600 bg-gray-50 font-body tracking-wide text-gray-900 antialiased transition dark:bg-gray-700 dark:text-gray-50"
      // Workaround to get React to keep `inert` prop. React 19 adds support for intert as a boolean.
      {...{ inert: onboardingIsVisible || modal !== null ? '' : undefined }}>
      <div className="w-full text-center">
        <Head key="default">
          <title>Toolpath</title>
          <meta name="googlebot" content="noindex" />
          <meta
            name="description"
            content="Automated CAM software for CNC machining."
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://toolpath.com" />
          <meta property="og:title" content="Toolpath" />
          <meta
            property="og:description"
            content="Automated CAM software for CNC machining."
          />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:domain" content="toolpath.com" />
          <meta name="twitter:title" content="Toolpath" />
          <meta
            name="twitter:description"
            content="Automated CAM software for CNC machining."
          />
          <meta name="twitter:site" content="@toolpathlabs" />
          <meta name="twitter:creator" content="@toolpathlabs" />
          <meta name="twitter:url" content="https://toolpath.com" />
        </Head>
        <div className="relative">
          {!hideNavigation && <NavigationMobile />}
          <div
            className={classNames(
              'flex min-h-screen w-full flex-col md:w-auto md:flex-row',
              className,
            )}>
            <div
              className={classNames(
                'absolute top-0 z-10 min-h-screen w-full shrink-0 md:relative md:z-0 md:block md:w-auto',
                {
                  'md:w-56': !hideNavigation && !navigationCollapsed,
                  'md:w-18': !hideNavigation && navigationCollapsed,
                  'pointer-events-none md:pointer-events-auto':
                    hideNavigation || mobileNavigationClosed,
                },
              )}>
              {!hideNavigation && <Navigation />}
            </div>
            <div
              className={classNames(
                'flex-1',
                {
                  'md:-ml-56': !hideNavigation && full && !navigationCollapsed,
                  'md:-ml-18': !hideNavigation && full && navigationCollapsed,
                },
                mainWrapperClassName,
              )}>
              <main
                className={classNames(
                  `hide-scrollbar text-left transition duration-1000 ease-in-out opacity-${opacity}`,
                  {
                    'mx-auto max-w-screen-xl px-4 sm:px-6 xl:px-6': !full,
                    'mx-0 w-full overflow-x-hidden px-0': full,
                  },
                  mainClassName,
                )}>
                {error ? (
                  <ErrorPage
                    message={String(error)}
                    previous={previous}
                    previousMessage={previousMessage}
                  />
                ) : (
                  <ErrorBoundary>{children}</ErrorBoundary>
                )}
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Layout
